import { useEffect, useState } from 'react'
import * as React from 'react'
import gql from 'graphql-tag'
import { Interweave } from 'interweave'
import { UrlMatcher } from 'interweave-autolink'
import { polyfill as interweaveSSRPolyfill } from 'interweave-ssr'
import Link from 'next/link'
import styled from 'styled-components'
import { NewsItemContent_NewsItemFragment } from 'generated/graphql'
import ErrorBoundary from '@app.components/ErrorBoundary'
import useExpendBtn from '@app.feature/channel/hook/useExpendBtn'
import { mobileBreakPoint } from 'app.styled/media'
import useTagEffect from '../../hook/useTagEffect'
import NewsItemImageSlider from '../NewsItemImageSlider'

gql`
  fragment NewsItemContent_NewsItem on NewsItem {
    id
    content
    images {
      nodes {
        id
        url
      }
    }
    embed {
      id
      imageUrl
      description
      domain
      url
    }
  }
`

type TProps = {
  data: NewsItemContent_NewsItemFragment
  expendContent?: boolean
  onContentClick?: React.MouseEventHandler<HTMLDivElement>
  onImageClick?: () => void
  type?: 'feed' | 'modal' | 'card'
  className?: string
}

const NewsItemContent: React.FC<TProps> = ({
  type = 'feed',
  data,
  expendContent = false,
  onContentClick,
  onImageClick,
  className,
}) => {
  // 서버렌더링에서 Interweave가 window/document를 체크하면서 HTML을 그리지 못함 ->ssr렌더러 사용
  if (
    typeof window === 'undefined' &&
    typeof globalThis.INTERWEAVE_SSR_POLYFILL !== 'function'
  ) {
    interweaveSSRPolyfill()
  }

  const [isExpended, descriptionRef, handleExpendBtn, handleCancleExpendBtn] =
    useExpendBtn(expendContent)

  const [embedImgState, setEmbedImgState] = useState({
    visible: false,
    width: 0,
    height: 0,
  })
  const isNoImage =
    type === 'card' && data.images.nodes.length === 0 && !data.embed

  useEffect(() => {
    if (typeof window !== 'object') return
    if (!data.embed?.imageUrl) return

    const embedImg = new Image()
    embedImg.onload = () => {
      setEmbedImgState({
        visible: embedImg.width !== 0,
        width: embedImg.width,
        height: embedImg.height,
      })
    }
    embedImg.src = data.embed.imageUrl
  }, [data.embed])
  useTagEffect(descriptionRef)

  return (
    <StyledWrapper
      className={className}
      data-type={type}
      data-expend={isExpended}
      data-no-image={isNoImage}
    >
      <div
        className={`description`}
        ref={descriptionRef}
        onClick={onContentClick}
      >
        <ErrorBoundary>
          <Interweave
            content={data.content}
            matchers={[new UrlMatcher('url')]}
          />
        </ErrorBoundary>
        {type === 'modal' && (
          <div
            className="expend-cancle-btn"
            onClick={() => {
              if (!descriptionRef.current) return
              descriptionRef.current.scrollTo(0, 0)
              handleCancleExpendBtn()
            }}
          >
            더보기 취소
          </div>
        )}
      </div>
      {isExpended === false && (
        <div
          className="expend-btn"
          onClick={type === 'card' ? onContentClick : handleExpendBtn}
        >
          ...더보기
        </div>
      )}
      {data.images.nodes.length > 0 && (
        <NewsItemImageSlider
          className="slider"
          data={data.images.nodes}
          type={type}
          onClick={onImageClick}
        />
      )}
      {data.embed && (
        <Link href={data.embed.url}>
          <a
            className="embed-card"
            data-small={embedImgState.width <= 600}
            target="_blank"
          >
            {embedImgState.visible && (
              <div className="img-wrapper">
                <img className="img" src={data.embed.imageUrl!} alt="" />
              </div>
            )}
            <div className="content-wrapper">
              <div className="embed-title">{data.embed.description}</div>
              <div className="embed-subtitle">{data.embed.domain}</div>
            </div>
          </a>
        </Link>
      )}
    </StyledWrapper>
  )
}

export default NewsItemContent

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  & > .description {
    padding: 0 20px 0px;
    line-height: 24px;
    height: 96px;
    overflow: hidden;
    overflow-wrap: anywhere;
    a {
      color: rgb(1, 160, 255) !important;
    }
    .ql-hashtag {
      color: ${({ theme }) => theme.color.primary_2};
      font-weight: 400;

      &:hover {
        cursor: pointer;
      }
    }
    .expend-cancle-btn {
      font-size: 12px;
      cursor: pointer;
    }
    &[data-expend='true'] {
      height: fit-content;
      margin-bottom: 12px;
    }
  }
  .slider {
    -webkit-tap-highlight-color: transparent;
  }
  & > .expend-btn {
    margin: 5px 20px 12px;
    color: #777;
    font-size: 12px;
    cursor: pointer;
  }
  .embed-card {
    background-color: #f5fafd;
    .content-wrapper {
      overflow: hidden;
      padding: 30px 20px;
      .embed-title {
        margin-bottom: 4px;
        vertical-align: middle;
        overflow: hidden;
        text-overflow: ellipsis;
        overflow-wrap: break-word;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
      .embed-subtitle {
        color: #9f9f9f;
        font-size: 14px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
    &[data-small='true'] {
      display: flex;
      .img-wrapper {
        flex-shrink: 0;
        padding: 20px 0 15px 20px;
        img {
          height: 95px;
        }
      }
      .content-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
    }
    &[data-small='false'] {
      .img-wrapper {
        position: relative;
        width: 100%;
        background-color: #333;
        &:after {
          content: '';
          display: block;
          padding-bottom: 60%;
        }
        .img {
          position: absolute;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center center;
        }
      }
    }
  }
  &[data-type='card'] {
    height: 412px;
    flex-grow: 1;
    .embed-card {
      .img-wrapper {
        max-height: 160px;
      }
      .content-wrapper {
        padding: 20px;
      }
    }
    .description {
      height: 24px;
      &[data-expend='true'] {
        margin-bottom: 12px;
      }
    }
    .expend-btn {
      margin: 2px 20px 16px;
    }
  }
  &[data-no-image='true'] {
    .description {
      height: 288px;
    }
  }
  &[data-expend='true'] {
    .description {
      height: fit-content;
      margin-bottom: 12px;
    }
  }
  ${mobileBreakPoint} {
    .embed-card {
      .content-wrapper {
        padding: 15px 20px 20px 15px;
      }
      &[data-small='true'] {
        .img-wrapper {
          padding: 15px 0 10px 20px;
          img {
            height: 75px;
          }
        }
      }
    }
    &[data-type='card'] {
      .description {
        font-size: 14px;
        height: 24px;
      }
      .expend-btn {
        margin: 0px 15px 10px;
      }
    }
    &[data-no-image='true'] {
      .description {
        height: 200px;
      }
    }
    &[data-type='modal'] {
      .description {
        z-index: 1;
        color: white;
        height: 24px;
        background-color: rgba(51, 51, 51, 0.7);
      }
      .expend-btn {
        color: white;
      }
      &[data-expend='true'] {
        .description {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: fit-content;
          max-height: 400px;
          overflow: scroll;
          padding-bottom: 20px;
        }
      }
    }
  }
`
